import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLang } from '../utils/LanguageContext';
import { useAuth } from '../utils/AuthContext';
import { t } from '../utils/translations';
import './DayCloseScreen.css';
import Loader from '../components/Loader';

// "Caisse du soir" — end-of-day summary the owner does every night.
function DayCloseScreen() {
  const [orders, setOrders] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counted, setCounted] = useState('');

  const { lang } = useLang();
  const { isMax } = useAuth();
  const tr = t[lang];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchToday(); }, []);

  function isToday(str) {
    const d = new Date(new Date(str).toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Casablanca' }));
    return d.toDateString() === today.toDateString();
  }

  async function fetchToday() {
    const [{ data }, { data: profs }] = await Promise.all([
      supabase.from('orders').select('*, order_items(*)').order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, full_name, email'),
    ]);
    setOrders((data || []).filter(o => isToday(o.created_at)));
    setTeam(profs || []);
    setLoading(false);
  }

  if (loading) return <Loader text={tr.loadingApp} />;

  const revenue = orders.reduce((s, o) => s + parseFloat(o.total), 0);
  const byPay = { cash: 0, card: 0, carnet: 0 };
  orders.forEach(o => { byPay[o.payment_method] = (byPay[o.payment_method] || 0) + parseFloat(o.total); });

  const profit = orders.reduce((s, o) =>
    s + (o.order_items || []).reduce((a, i) =>
      a + (parseFloat(i.price) - parseFloat(i.cost || 0)) * i.quantity, 0), 0);
  const hasCost = orders.some(o => (o.order_items || []).some(i => parseFloat(i.cost || 0) > 0));

  const countedNum = parseFloat(counted);
  const diff = counted === '' || isNaN(countedNum) ? null : countedNum - byPay.cash;

  const today = new Date().toLocaleDateString(lang === 'ar' ? 'ar-MA' : 'fr-MA', {
    weekday: 'long', day: '2-digit', month: 'long', timeZone: 'Africa/Casablanca'
  });

  return (
    <div className="dayclose">
      <h2 className="dc-title">🧾 {tr.dayClose}</h2>
      <p className="dc-date">{today}</p>

      <div className="dc-hero">
        <span className="dc-hero-label">{tr.todayRevenue}</span>
        <span className="dc-hero-value">{revenue.toFixed(0)} {tr.currency}</span>
        <span className="dc-hero-sub">{orders.length} {tr.totalOrders.toLowerCase()}</span>
      </div>

      <div className="dc-card">
        <h3>{tr.paymentMethod}</h3>
        <div className="dc-row"><span>💵 {tr.payCash}</span><span>{byPay.cash.toFixed(0)} {tr.currency}</span></div>
        <div className="dc-row"><span>💳 {tr.payCard}</span><span>{byPay.card.toFixed(0)} {tr.currency}</span></div>
        {byPay.carnet > 0 && (
          <div className="dc-row"><span>📒 {tr.payCarnet}</span><span>{byPay.carnet.toFixed(0)} {tr.currency}</span></div>
        )}
      </div>

      {hasCost && (
        <div className="dc-card dc-profit">
          <div className="dc-row"><span>📈 {tr.todayProfit}</span><span>{profit.toFixed(0)} {tr.currency}</span></div>
        </div>
      )}

      {isMax && (() => {
        const byCashier = {};
        orders.forEach(o => {
          const k = o.created_by || 'unknown';
          byCashier[k] = (byCashier[k] || 0) + parseFloat(o.total);
        });
        const rows = Object.entries(byCashier).sort((a, b) => b[1] - a[1]);
        const nameOf = (id) => {
          if (id === 'unknown') return '—';
          const p = team.find(m => m.id === id);
          return p ? (p.full_name || p.email || id.slice(0, 6)) : id.slice(0, 6);
        };
        return (
          <div className="dc-card">
            <h3>👥 {tr.byCashier}</h3>
            {rows.map(([id, total]) => (
              <div className="dc-row" key={id}>
                <span>{nameOf(id)}</span>
                <span>{total.toFixed(0)} {tr.currency}</span>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="dc-card">
        <h3>{tr.cashCount}</h3>
        <p className="dc-hint">{tr.cashCountHint}</p>
        <div className="dc-row">
          <span>{tr.expectedCash}</span>
          <span>{byPay.cash.toFixed(0)} {tr.currency}</span>
        </div>
        <div className="dc-row">
          <span>{tr.countedCash}</span>
          <input
            className="dc-input"
            type="number"
            inputMode="numeric"
            placeholder="0"
            value={counted}
            onChange={e => setCounted(e.target.value)}
          />
        </div>
        {diff !== null && (
          <div className={`dc-diff ${diff === 0 ? 'ok' : diff > 0 ? 'over' : 'short'}`}>
            {tr.difference}: {diff > 0 ? '+' : ''}{diff.toFixed(0)} {tr.currency}
            {diff === 0 ? ` ✅` : diff > 0 ? ` (${tr.over})` : ` (${tr.short})`}
          </div>
        )}
      </div>
    </div>
  );
}

export default DayCloseScreen;
